import Navigation from "./navigation";
import Image from "next/image";
import Magnet from "@/components/magnet";
import { useLayoutEffect, useRef } from "react";
import { gsap } from "gsap/dist/gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { useLanguage } from "@/contexts/LanguageContext";
import { useRouter } from "next/router";

gsap.registerPlugin(ScrollTrigger);

export default function Hero() {
  const { t } = useLanguage();
  const router = useRouter();

  // Refs pour les éléments à animer
  const badgeRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const mainImageRef = useRef<HTMLDivElement>(null);
  const sideImageRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    // Set initial states (hidden)
    gsap.set(
      [
        badgeRef.current,
        titleRef.current,
        descriptionRef.current,
        buttonRef.current,
      ],
      {
        opacity: 0,
        y: 30,
      }
    );

    gsap.set([mainImageRef.current, sideImageRef.current], {
      opacity: 0,
      y: 60,
    });

    // Create timeline for initial animations - plus rapide
    const tl = gsap.timeline({ delay: 0.1 });

    // Animate texts one by one from top to bottom - animations plus rapides
    tl.to(badgeRef.current, {
      opacity: 1,
      y: 0,
      duration: 0.5,
      ease: "power2.out",
    })
      .to(
        titleRef.current,
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: "power2.out",
        },
        "-=0.3"
      )
      .to(
        descriptionRef.current,
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: "power2.out",
        },
        "-=0.3"
      )
      .to(
        buttonRef.current,
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: "power2.out",
        },
        "-=0.3"
      )
      .to(
        [mainImageRef.current, sideImageRef.current],
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: "power2.out",
          stagger: 0.15,
        },
        "-=0.4"
      );

    // Animations de scroll - Parallax pour les images
    gsap.fromTo(
      mainImageRef.current,
      { y: 0 },
      {
        y: 50,
        x: -5,
        scale: 1.1,
        ease: "none",
        scrollTrigger: {
          trigger: heroRef.current,
          start: "top top",
          end: "bottom top",
          scrub: 1.5,
        },
      }
    );

    gsap.fromTo(
      sideImageRef.current,
      { y: 0 },
      {
        y: -20,
        x: 5,
        scale: 1.1,
        ease: "none",
        scrollTrigger: {
          trigger: heroRef.current,
          start: "top top",
          end: "bottom top",
          scrub: 1.5,
        },
      }
    );

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);
  return (
    <header
      ref={heroRef}
      className="bg-gradient-to-b from-white from-20% to-blue-300 flex flex-col gap-16 items-center justify-between overflow-hidden px-8 xl:px-0 z-[51] relative"
    >
      <Navigation />
      <div className="flex flex-col items-start md:items-center md:justify-center gap-4 mt-6 md:mt-10 z-10">
        <div
          ref={badgeRef}
          className="bg-blue-50 px-5 py-2 rounded-full uppercase tracking-wide text-sm text-blue-900 font-medium mb-6"
        >
          {t("hero.badge")}
        </div>
        <h1
          ref={titleRef}
          className="text-4xl font-medium md:text-center leading-10"
        >
          {t("hero.title")}
        </h1>
        <p
          ref={descriptionRef}
          className="md:text-center text-md max-w-3xl mx-auto"
        >
          {t("hero.description")}
        </p>
        <div ref={buttonRef} className="mt-6">
          <Magnet padding={150} disabled={false} magnetStrength={20}>
            <button
              onClick={() => router.push("/start")}
              aria-label="Get started with Featcal for free"
              className="bg-blue-600 border-2 border-blue-600 hover:bg-transparent hover:text-blue-800 hover:border-blue-800 hover:shadow-none transition-all duration-300 shadow-xl shadow-blue-500/20 text-white px-6 py-4 rounded-full"
            >
              {t("hero.startForFree")}
            </button>
          </Magnet>
        </div>
      </div>
      <div className="flex items-end">
        <div
          ref={mainImageRef}
          className="md:relative md:translate-x-10 xl:translate-x-30 md:max-w-6xl md:mx-auto bg-gradient-to-b from-white/20 to-white/50 rounded-t-3xl p-4 md:p-6 !pb-0 border-t border-white shadow-2xl shadow-black/5 z-10"
        >
          <Image
            src="/images/customer-admin.webp"
            alt="Healthcare professional dashboard showing patient tracking, meal plans, and progress analytics for doctors, nutritionists, and coaches"
            width={1000}
            height={600}
            priority
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1000px"
            className="rounded-t-lg w-full max-md:h-full max-md:object-cover"
          />
        </div>

        <div className="hidden md:block">
          <div
            ref={sideImageRef}
            className="xl:w-[270px] -translate-x-10 xl:-translate-x-30 relative bg-gradient-to-b from-white/20 to-white/50 rounded-t-4xl p-3 pb-0 border-t border-white shadow-[-10px_-10px_30px_rgba(0,0,0,0.1)] backdrop-blur-sm z-20"
          >
            <Image
              src="/images/my-day.webp"
              alt="Personalized meal plan interface for nutritionists to create and share with patients"
              width={270}
              height={540}
              priority
              sizes="(max-width: 768px) 0px, 270px"
              className="w-full rounded-t-2xl"
            />
          </div>
        </div>
      </div>
    </header>
  );
}
